import React from "react";
import { DEFAULT_OPEN_ATTRIBUTE } from "./disclosure.shared";
import styles from "./disclosure.module.css";
import { withSafeRender } from "../../atoms/safe-render";

/**
 * The `<details>` / `<summary>` wrapper every collapsible region in the lineup is built from: grid
 * sections (GS1-GS8), an RSN's See details (GR5), a pack's Show More (GP12) and the chip overflow
 * (SR7).
 */
export interface DisclosureProps {
  /** Rendered inside the `<summary>`: a section heading, a Show More control. */
  summary: React.ReactNode;
  /** GS7 — the `open` attribute the server emits. See the note above. */
  defaultOpen?: boolean;
  /** Where the summary draws its own affordance (`show-more-toggle`). */
  hideChevron?: boolean;
  /** Set it when something needs to point at the region, e.g. a skip link. */
  id?: string;
  /** Layout only. */
  className?: string;
  /** Layout only, on the `<summary>`. */
  summaryClassName?: string;
  children: React.ReactNode;
}

function DisclosureImpl({
  summary,
  defaultOpen = false,
  hideChevron = false,
  id,
  className,
  summaryClassName,
  children
}: DisclosureProps) {
  return (
    <details
      className={[styles.details, className].filter(Boolean).join(" ")}
      id={id}
      open={defaultOpen}
      {...(defaultOpen && { [DEFAULT_OPEN_ATTRIBUTE]: "" })}
    >
      <summary className={[styles.summary, summaryClassName].filter(Boolean).join(" ")}>
        <span className={styles.summaryContent}>{summary}</span>
        {!hideChevron && (
          /* GS8 — up when open, down when closed, rotated by CSS off
             `details[open]`. Hidden from readers: `<summary>` already
             announces the state, and a named chevron says it twice. */
          /* The frame's own 20 x 12 glyph (`15527:28971`). */
          <svg aria-hidden="true" className={styles.chevron} focusable="false" viewBox="0 0 20 12">
            <path
              d="M0.016 1.98c0-.498.181-1.004.552-1.388.742-.76 1.941-.76 2.675 0L9.998 7.598l6.77-7.022c.742-.768 1.934-.768 2.676 0 .741.767.741 2 0 2.768l-7.576 7.856A2.6 2.6 0 0 1 9.99 12c-.702 0-1.389-.286-1.878-.792L.552 3.368A1.99 1.99 0 0 1 0 1.988"
              fill="currentColor"
            />
          </svg>
        )}
      </summary>
      {children}
    </details>
  );
}
export const Disclosure = withSafeRender(DisclosureImpl, "Disclosure");

export default Disclosure;
